import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { SeasonTime } from '../../services/seasonTime.service';
import type { CropType } from '../../services/cropType.service';
import type { Region } from '../../services/region.service';
import type { SeasonStatus } from '../../utils/seasonStatus';
import { getSeasonStatus } from '../../utils/seasonStatus';
import type { CropStatusFilters, ResultRow } from './types';
import { dayOfYear, seasonBarSegments } from './cropStatusUtils';

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const GRID_COLS = MONTH_DAYS.map(d => `${d}fr`).join(' ');
const YEAR_DAYS = 365;

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  seasonTimes: SeasonTime[];
  cropTypes: CropType[];
  regions: Region[];
  results: ResultRow[];
  filters: CropStatusFilters;
  onViewCustomers: (_cropTypeId: string, _regionId: string, _status: SeasonStatus) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayPct(): number {
  const today = new Date();
  return (dayOfYear(today.getMonth() + 1, today.getDate()) / YEAR_DAYS) * 100;
}

function thresholdEndPct(thresholdDays: number): number {
  const today = new Date();
  const doy = dayOfYear(today.getMonth() + 1, today.getDate());
  return Math.min(((doy + thresholdDays) / YEAR_DAYS) * 100, 100);
}

function barColor(status: SeasonStatus): string {
  if (status === 'in-season') return 'bg-green-500';
  if (status === 'upcoming')  return 'bg-amber-400';
  return 'bg-gray-300';
}

function badgeStyle(status: SeasonStatus): string {
  if (status === 'in-season') return 'bg-green-100 text-green-800';
  if (status === 'upcoming')  return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-600';
}

// Moved inside component to use translated months

function durationDays(s: SeasonTime): number {
  const y = new Date().getFullYear();
  const start = new Date(y, s.startMonth - 1, s.startDay);
  const end   = new Date(y, s.endMonth   - 1, s.endDay);
  let diff = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
  if (diff < 0) diff += YEAR_DAYS;
  return diff;
}

function daysUntilStart(s: SeasonTime): number {
  const today = new Date();
  const y = today.getFullYear();
  let start = new Date(y, s.startMonth - 1, s.startDay);
  let diff = Math.round((start.getTime() - today.getTime()) / 86_400_000);
  if (diff < 0) {
    start = new Date(y + 1, s.startMonth - 1, s.startDay);
    diff = Math.round((start.getTime() - today.getTime()) / 86_400_000);
  }
  return diff;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TimelineView({ seasonTimes, cropTypes, regions, results, filters, onViewCustomers }: Props) {
  const { t, i18n } = useTranslation();
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [tooltipRect, setTooltipRect] = useState<DOMRect | null>(null); // eslint-disable-line no-undef

  // RTL detection
  const isRTL = i18n.dir() === 'rtl';

  // Get translated month names
  const months = Array.from({ length: 12 }, (_, i) => t(`cropStatus.timeline.months.${i + 1}`) as string);

  function formatDate(month: number, day: number): string {
    return `${months[month - 1]} ${day}`;
  }

  function handleLabelClick(key: string, e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    // Just select the row, don't show popup
    setActiveKey(key);
    setTooltipRect(null);
  }

  function handleRowClick(key: string, e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    if (activeKey === key && tooltipRect) {
      // Tooltip is already showing on this row: close it
      setTooltipRect(null);
    } else {
      // Row not selected, or tooltip not showing, or different row: select and show tooltip
      setActiveKey(key);
      setTooltipRect(e.currentTarget.getBoundingClientRect());
    }
  }

  const today = new Date();
  const todayMonth = today.getMonth();
  const tPct  = todayPct();
  const thPct = thresholdEndPct(filters.upcomingThresholdDays);

  // ── Customer count per (cropTypeId × regionId) from query results ──
  const countMap = new Map<string, number>();
  for (const r of results) {
    const k = `${r.cropTypeId}|${r.regionId}`;
    countMap.set(k, (countMap.get(k) ?? 0) + 1);
  }

  // ── Filter & sort rows ─────────────────────────────────────────────
  let rows = seasonTimes as (SeasonTime & { status: SeasonStatus })[];

  if (filters.cropTypeId) rows = rows.filter(r => r.cropTypeId === filters.cropTypeId);
  if (filters.regionId)   rows = rows.filter(r => r.regionId   === filters.regionId);

  const rowsWithStatus = rows.map(r => ({
    ...r,
    status: getSeasonStatus(r, today, filters.upcomingThresholdDays),
  }));

  const filtered = filters.status
    ? rowsWithStatus.filter(r => r.status === filters.status)
    : rowsWithStatus;

  const sorted = [...filtered].sort((a, b) => {
    const ctA = cropTypes.find(c => c.id === a.cropTypeId)?.name ?? '';
    const ctB = cropTypes.find(c => c.id === b.cropTypeId)?.name ?? '';
    if (ctA !== ctB) return ctA.localeCompare(ctB);
    const rA = regions.find(r => r.id === a.regionId)?.name ?? '';
    const rB = regions.find(r => r.id === b.regionId)?.name ?? '';
    return rA.localeCompare(rB);
  });

  function handleViewCustomers(row: (typeof sorted)[0]) {
    setActiveKey(null);
    onViewCustomers(row.cropTypeId, row.regionId, row.status);
  }

  // ── Empty state ──────────────────────────────────────────────────────
  if (sorted.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 px-6 py-10 text-center">
        <p className="font-body text-sm font-semibold text-gray-600">{t('cropStatus.timeline.empty')}</p>
        <p className="mt-1 font-body text-sm text-gray-400">{t('cropStatus.timeline.emptyHelp')}</p>
      </div>
    );
  }

  // ── Active row data (for tooltip / bottom-sheet) ─────────────────────
  const activeRow = activeKey ? sorted.find(r => `${r.cropTypeId}|${r.regionId}` === activeKey) : null;

  return (
    <div>
      {/* ── Timeline widget ── */}
      <div className="mb-4 overflow-hidden rounded-lg border border-gray-200">
        {/* Scrollable timeline area */}
        <div className="timeline-scroll overflow-x-auto">
          <div className="flex min-w-full">

            {/* Left label column (now scrolls with timeline) */}
            <div className="w-[150px] shrink-0 border-e border-gray-200 bg-gray-50 sm:w-[230px]">
              <div className="flex h-8.5 items-center border-b-2 border-gray-200 px-3 font-body text-[0.72rem] font-semibold uppercase tracking-wider text-gray-400">
                {t('cropStatus.timeline.cropRegion')}
              </div>
              {sorted.map((row, i) => {
                const key = `${row.cropTypeId}|${row.regionId}`;
                const ctName = cropTypes.find(c => c.id === row.cropTypeId)?.name ?? row.cropTypeId;
                const rName  = regions.find(r => r.id === row.regionId)?.name ?? row.regionId;
                const isActive = activeKey === key;
                return (
                  <div
                    key={key}
                    className={`flex h-[46px] items-center border-b border-gray-100 px-3 font-body text-xs sm:text-sm cursor-pointer
                      ${i === sorted.length - 1 ? 'border-b-0' : ''}
                      ${isActive ? 'bg-amber-50 font-bold text-gray-900' : 'font-semibold text-gray-700'}`}
                    onClick={e => handleLabelClick(key, e)}
                  >
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                      {ctName} × {rName}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Timeline bars area */}
            <div className="flex-1">
              <div className="min-w-[1200px] md:min-w-0">

              {/* Month header */}
              <div
                className="sm:sticky sm:top-0 z-10 border-b-2 border-gray-200 bg-gray-50"
                style={{ display: 'grid', gridTemplateColumns: GRID_COLS }}
              >
                {months.map((m, i) => (
                  <div
                    key={m}
                    className={[
                      'text-center font-body text-[0.72rem] font-semibold h-8 flex items-center justify-center',
                      i < 11 ? `border-e border-dashed ${i === todayMonth ? 'border-green-200' : 'border-gray-200'}` : '',
                      i === todayMonth ? 'bg-green-50 font-bold text-green-700' : 'text-gray-500',
                    ].join(' ')}
                  >
                    {m}
                  </div>
                ))}
              </div>

              {/* Bar rows */}
              <div className="relative">
                {/* Today line */}
                <div
                  className="pointer-events-none absolute inset-y-0 z-10 w-0.5 bg-red-600"
                  style={isRTL ? { right: `${tPct}%` } : { left: `${tPct}%` }}
                >
                  <span className="absolute start-1.5 top-2 whitespace-nowrap font-body text-[0.62rem] font-black text-red-600">
                    {t('cropStatus.timeline.today')}
                  </span>
                </div>

                {/* Upcoming threshold shading */}
                <div
                  className="pointer-events-none absolute inset-y-0 z-[1] bg-amber-400/10"
                  style={isRTL ? { right: `${tPct}%`, width: `${thPct - tPct}%` } : { left: `${tPct}%`, width: `${thPct - tPct}%` }}
                />
                {/* Threshold boundary dashed lines */}
                <div
                  className="pointer-events-none absolute inset-y-0 z-[2] border-s border-dashed border-amber-400"
                  style={isRTL ? { right: `${tPct}%` } : { left: `${tPct}%` }}
                />
                <div
                  className="pointer-events-none absolute inset-y-0 z-[2] border-s border-dashed border-amber-400"
                  style={isRTL ? { right: `${thPct}%` } : { left: `${thPct}%` }}
                />

                {sorted.map((row, i) => {
                  const key = `${row.cropTypeId}|${row.regionId}`;
                  const isActive = activeKey === key;
                  const segments = seasonBarSegments(row.startMonth, row.startDay, row.endMonth, row.endDay);
                  const ctName = cropTypes.find(c => c.id === row.cropTypeId)?.name ?? row.cropTypeId;
                  const rName  = regions.find(r => r.id === row.regionId)?.name ?? row.regionId;

                  return (
                    <div
                      key={key}
                      className={[
                        'relative h-[46px] cursor-pointer',
                        i < sorted.length - 1 ? 'border-b border-gray-100' : '',
                        isActive ? 'bg-amber-50' : '',
                      ].join(' ')}
                      onClick={e => handleRowClick(key, e)}
                      aria-label={`${ctName} × ${rName}`}
                    >
                      {/* Bars */}
                      {segments.map((seg, si) => {
                        let outlineColor = '';
                        if (isActive) {
                          if (row.status === 'in-season') outlineColor = 'outline outline-2 outline-offset-2 outline-green-700';
                          else if (row.status === 'upcoming') outlineColor = 'outline outline-2 outline-offset-2 outline-amber-600';
                          else outlineColor = 'outline outline-2 outline-offset-2 outline-gray-500';
                        }
                        return (
                          <div
                            key={si}
                            className={[
                              'absolute top-[10px] h-6 rounded-md',
                              barColor(row.status),
                              outlineColor,
                            ].join(' ')}
                            style={isRTL ? { right: `${seg.left}%`, width: `${seg.width}%` } : { left: `${seg.left}%`, width: `${seg.width}%` }}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Desktop tooltip (portal — avoids overflow clipping) ── */}
      {activeKey && tooltipRect && (() => {
        const row = sorted.find(r => `${r.cropTypeId}|${r.regionId}` === activeKey);
        if (!row) return null;
        const ctName = cropTypes.find(c => c.id === row.cropTypeId)?.name ?? row.cropTypeId;
        const rName  = regions.find(r => r.id === row.regionId)?.name ?? row.regionId;
        const count  = countMap.get(activeKey) ?? 0;

        const tooltipW = 256;
        const gap = 8;
        let left = tooltipRect.left + tooltipRect.width * 0.25;
        if (left + tooltipW > window.innerWidth - 12) left = window.innerWidth - tooltipW - 12;
        if (left < 12) left = 12;
        const top = tooltipRect.top + tooltipRect.height + gap;

        return createPortal(
          <div
            className="hidden sm:block"
            style={{ position: 'fixed', top, left, zIndex: 9999, width: tooltipW }}
            onClick={e => e.stopPropagation()}
          >
            <div className="rounded-xl border border-gray-200 bg-white p-3.5 shadow-xl">
              {/* Caret (top pointing up) */}
              <div
                className={`absolute -top-[8px] h-3.5 w-3.5 bg-white ${isRTL ? '-rotate-45' : 'rotate-45'}`}
                style={{
                  ...(isRTL
                    ? { right: '24px', borderTop: '1.5px solid #e5e7eb', borderRight: '1.5px solid #e5e7eb' }
                    : { left: '24px', borderTop: '1.5px solid #e5e7eb', borderLeft: '1.5px solid #e5e7eb' }
                  ),
                }}
              />

              {/* Header */}
              <div className="mb-2.5 flex min-w-0 items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-body text-sm font-bold text-gray-900">{ctName} × {rName}</div>
                  <div className="font-body text-xs text-gray-400">
                    {formatDate(row.startMonth, row.startDay)} – {formatDate(row.endMonth, row.endDay)}
                  </div>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 font-body text-[0.72rem] font-bold ${badgeStyle(row.status)}`}>
                  {row.status === 'in-season'
                    ? t('cropStatus.statusInSeason')
                    : row.status === 'upcoming'
                      ? t('cropStatus.statusUpcoming')
                      : t('cropStatus.timeline.offSeason')}
                </span>
              </div>

              {/* Detail rows */}
              <div className="mb-2.5 flex flex-col gap-1.5 border-t border-gray-100 pt-2 font-body text-sm">
                {row.status === 'upcoming' && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('cropStatus.timeline.startsIn')}</span>
                    <span className="font-bold text-gray-900">{t('cropStatus.timeline.days', { count: daysUntilStart(row) })}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('cropStatus.timeline.duration')}</span>
                  <span className="font-semibold text-gray-700">{t('cropStatus.timeline.days', { count: durationDays(row) })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('cropStatus.timeline.customers')}</span>
                  <span className="font-semibold text-gray-700">{t('cropStatus.timeline.customersCount', { count })}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleViewCustomers(row)}
                className="w-full rounded-lg bg-green-700 py-1.5 font-body text-sm font-bold text-white hover:bg-green-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
              >
                {t('cropStatus.timeline.viewCustomers', { count })}
              </button>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* ── Legend ── */}
      <div className="mb-1 flex flex-wrap items-center gap-x-4 gap-y-1.5 font-body text-xs text-gray-500 sm:text-[0.78rem]">
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-3.5 shrink-0 rounded-[3px] bg-green-500" />
          {t('cropStatus.statusInSeason')}
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-3.5 shrink-0 rounded-[3px] bg-amber-400" />
          {t('cropStatus.statusUpcoming')}
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-3.5 shrink-0 rounded-[3px] bg-gray-300" />
          {t('cropStatus.timeline.offSeason')}
        </div>
        <div className="ms-2 flex items-center gap-1.5">
          <div className="h-4 w-0.5 shrink-0 rounded-sm bg-red-600" />
          <span className="font-semibold text-red-600">
            {t('cropStatus.timeline.todayLabel', {
              date: `${months[todayMonth]} ${today.getDate()}`,
            })}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-10 shrink-0 rounded-sm border border-amber-400 bg-amber-200/40" />
          <span>{t('cropStatus.timeline.thresholdLabel', { days: filters.upcomingThresholdDays })}</span>
        </div>
      </div>

      {/* ── Mobile bottom sheet ── */}
      {activeRow && tooltipRect && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/30 sm:hidden"
            onClick={() => setActiveKey(null)}
          />
          <div className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl bg-white p-5 shadow-2xl sm:hidden">
            <div className="mb-3 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-body text-sm font-bold text-gray-900 truncate">
                  {(cropTypes.find(c => c.id === activeRow.cropTypeId)?.name ?? activeRow.cropTypeId)}
                  {' × '}
                  {(regions.find(r => r.id === activeRow.regionId)?.name ?? activeRow.regionId)}
                </div>
                <div className="font-body text-xs text-gray-400">
                  {formatDate(activeRow.startMonth, activeRow.startDay)} – {formatDate(activeRow.endMonth, activeRow.endDay)}
                </div>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 font-body text-[0.72rem] font-bold ${badgeStyle(activeRow.status)}`}>
                {activeRow.status === 'in-season'
                  ? t('cropStatus.statusInSeason')
                  : activeRow.status === 'upcoming'
                    ? t('cropStatus.statusUpcoming')
                    : t('cropStatus.timeline.offSeason')}
              </span>
              <button
                type="button"
                onClick={() => setActiveKey(null)}
                className="ms-1 shrink-0 font-body text-gray-400 hover:text-gray-600"
                aria-label={t('common.back')}
              >
                ✕
              </button>
            </div>

            <div className="mb-3 flex flex-col gap-2 border-t border-gray-100 pt-3 font-body text-sm">
              {activeRow.status === 'upcoming' && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('cropStatus.timeline.startsIn')}</span>
                  <span className="font-bold text-gray-900">
                    {t('cropStatus.timeline.days', { count: daysUntilStart(activeRow) })}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">{t('cropStatus.timeline.duration')}</span>
                <span className="font-semibold text-gray-700">
                  {t('cropStatus.timeline.days', { count: durationDays(activeRow) })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('cropStatus.timeline.customers')}</span>
                <span className="font-semibold text-gray-700">
                  {t('cropStatus.timeline.customersCount', { count: countMap.get(activeKey!) ?? 0 })}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => handleViewCustomers(activeRow)}
              className="w-full rounded-md bg-green-700 py-2.5 font-body text-sm font-bold text-white hover:bg-green-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
            >
              {t('cropStatus.timeline.viewCustomers', { count: countMap.get(activeKey!) ?? 0 })}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
